<template>
  <section
    class="relative isolate flex min-h-[min(82vh,580px)] flex-col overflow-x-clip border-b border-slate-200 dark:border-slate-800 sm:min-h-[min(78vh,640px)]"
    aria-label="Hero"
  >
    <!-- Full-bleed banner background (overlays only; no overflow-hidden on section — avoids clipping logo/shadows/CTAs) -->
    <div class="absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
      <template v-if="hasBannerImage">
        <img
          :src="bannerUrl"
          :alt="name ? `${name} — banner` : ''"
          class="absolute inset-0 h-full w-full object-cover object-center"
          loading="eager"
          fetchpriority="high"
        >
        <!-- Light scrim + softer gradients (photo shows through more; text still readable) -->
        <div class="absolute inset-0 bg-slate-950/35 dark:bg-slate-950/40" />
        <div
          class="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/50 to-slate-950/25 dark:from-slate-950/80 dark:via-slate-950/55 dark:to-slate-950/30"
        />
        <div
          class="absolute inset-0 bg-gradient-to-r from-slate-950/45 via-slate-950/10 to-transparent md:from-slate-950/40"
        />
      </template>
      <template v-else>
        <div
          class="absolute inset-0 bg-gradient-to-br from-slate-100 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900"
        />
        <div
          class="absolute inset-0 opacity-[0.12] dark:opacity-[0.18]"
          :style="noBannerTintStyle"
        />
      </template>
    </div>

    <!-- Foreground: logo, name, tagline, CTAs (centered vertically + generous padding so nothing clips) -->
    <div
      class="relative z-10 flex w-full flex-1 flex-col justify-center px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-16"
    >
      <div class="mx-auto w-full max-w-7xl">
        <div
          class="mx-auto flex max-w-3xl flex-col items-center text-center md:mx-0 md:items-start md:text-left"
        >
          <!-- Logo + status -->
          <div
            class="mb-6 flex flex-col items-center gap-5 md:mb-8 md:items-start"
          >
            <button
              v-if="logoUrl && canOpenOwnerLogoModal"
              type="button"
              class="flex h-24 w-24 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-2 shadow-xl sm:h-28 sm:w-28 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              :class="[
                hasBannerImage ? 'border-white/30 bg-white/95 dark:bg-slate-900/90 focus-visible:ring-white focus-visible:ring-offset-slate-950' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800 focus-visible:ring-slate-900 dark:focus-visible:ring-slate-200',
              ]"
              :aria-label="$t('public.ownerEditLogoAria')"
              data-testid="public-hero-logo"
              @click="openOwnerLogoModal"
            >
              <img
                :src="logoUrl"
                :alt="name || 'Business'"
                class="h-full w-full object-contain"
                width="112"
                height="112"
              >
            </button>
            <div
              v-else-if="logoUrl"
              class="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 shadow-xl sm:h-28 sm:w-28"
              :class="hasBannerImage ? 'border-white/30 bg-white/95 dark:bg-slate-900/90' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800'"
              data-testid="public-hero-logo"
            >
              <img
                :src="logoUrl"
                :alt="name || 'Business'"
                class="h-full w-full object-contain"
                width="112"
                height="112"
              >
            </div>
            <button
              v-else-if="viewer?.is_owner && ownerViewMode && canOpenOwnerLogoModal"
              type="button"
              class="max-w-[220px] cursor-pointer rounded border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              :class="hasBannerImage ? 'focus-visible:ring-white focus-visible:ring-offset-slate-950' : 'focus-visible:ring-slate-900 dark:focus-visible:ring-slate-200'"
              :aria-label="$t('public.ownerEditLogoAria')"
              @click="openOwnerLogoModal"
            >
              <div
                class="rounded border border-dashed border-white/40 bg-black/25 px-4 py-4 backdrop-blur-sm md:border-slate-300/60 md:bg-slate-200/50 dark:md:border-slate-600/60 dark:md:bg-slate-700/20"
              >
                <p
                  class="text-left text-sm font-mono"
                  :class="hasBannerImage ? 'text-slate-100' : 'text-slate-600 dark:text-slate-400'"
                >
                  {{ $t('public.noLogoSetOwnerMessage') }}
                </p>
              </div>
            </button>
            <div
              v-else-if="viewer?.is_owner && ownerViewMode"
              class="max-w-[220px] rounded border border-dashed border-white/40 bg-black/25 px-4 py-4 backdrop-blur-sm md:border-slate-300/60 md:bg-slate-200/50 dark:md:border-slate-600/60 dark:md:bg-slate-700/20"
            >
              <p
                class="text-left text-sm font-mono"
                :class="hasBannerImage ? 'text-slate-100' : 'text-slate-600 dark:text-slate-400'"
              >
                {{ $t('public.noLogoSetOwnerMessage') }}
              </p>
            </div>
            <div
              v-else
              class="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl border-2 shadow-lg sm:h-28 sm:w-28"
              :class="hasBannerImage ? 'border-white/25 bg-white/10 backdrop-blur-md' : 'border-slate-200 dark:border-slate-600'"
              :style="!hasBannerImage ? fallbackIconBoxStyle : undefined"
            >
              <span
                class="material-symbols-outlined text-5xl sm:text-[3.25rem]"
                :style="{ color: primaryColor || '#2563eb' }"
              >storefront</span>
            </div>
            <PublicOpenStatusBadge
              :operating-hours="operatingHours"
              :variant="hasBannerImage ? 't5-banner' : 't5-hero'"
            />
          </div>

          <p
            v-if="establishedYearDisplay"
            class="mb-2 font-condensed text-xs font-bold uppercase tracking-widest"
            :class="hasBannerImage ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'"
          >
            {{ $t('public.heroEstYear', { year: establishedYearDisplay }) }}
          </p>

          <h1
            class="mb-4 font-condensed text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
            :class="hasBannerImage ? 'text-white drop-shadow-sm' : 'text-slate-900 dark:text-slate-100'"
          >
            {{ heroTitle }}
          </h1>

          <p
            v-if="heroSubtext"
            class="mb-8 max-w-2xl text-lg leading-relaxed sm:text-xl"
            :class="hasBannerImage ? 'text-slate-100/95' : 'text-slate-600 dark:text-slate-400'"
          >
            {{ heroSubtext }}
          </p>
          <div
            v-else-if="viewer?.is_owner && ownerViewMode"
            class="mb-8 max-w-md rounded border border-dashed px-4 py-4 md:text-left"
            :class="hasBannerImage ? 'border-white/35 bg-black/20 backdrop-blur-sm' : 'border-slate-300/60 bg-slate-200/50 dark:border-slate-600/60 dark:bg-slate-700/20'"
          >
            <p
              class="text-sm font-mono"
              :class="hasBannerImage ? 'text-slate-100' : 'text-slate-600 dark:text-slate-400'"
            >
              {{ $t('public.noTaglineOrDescriptionSetOwnerMessage') }}
            </p>
          </div>

          <div class="flex flex-wrap items-center justify-center gap-3 pb-1 md:justify-start">
            <a
              href="#menu"
              class="inline-flex min-h-[44px] items-center justify-center rounded-lg px-8 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:text-base"
              :class="
                hasBannerImage
                  ? 'focus-visible:ring-white focus-visible:ring-offset-slate-950'
                  : 'focus-visible:ring-slate-900 dark:focus-visible:ring-slate-200'
              "
              :style="{ backgroundColor: primaryColor || '#2563eb' }"
            >
              {{ viewListLabel }}
            </a>
            <a
              v-if="hasAbout"
              href="#about"
              class="inline-flex min-h-[44px] items-center justify-center rounded-lg border px-8 py-3.5 text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:text-base"
              :class="secondaryCtaClass"
            >
              {{ $t('public.t3NavAbout') }}
            </a>
            <div
              v-else-if="viewer?.is_owner && ownerViewMode"
              class="max-w-[200px] rounded-lg border border-dashed px-4 py-3"
              :class="hasBannerImage ? 'border-white/35 bg-black/20 backdrop-blur-sm' : 'border-slate-300/60 bg-slate-200/50 dark:border-slate-600/60 dark:bg-slate-700/20'"
            >
              <p
                class="text-xs font-mono"
                :class="hasBannerImage ? 'text-slate-100' : 'text-slate-600 dark:text-slate-400'"
              >
                {{ $t('public.ownerNavAboutUnavailable') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import PublicOpenStatusBadge from '@/components/public/PublicOpenStatusBadge.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()

const props = defineProps({
  name: { type: String, default: '' },
  tagline: { type: String, default: '' },
  description: { type: String, default: '' },
  bannerUrl: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  /** Shown as “Est. YYYY” when set (matches other templates). */
  yearEstablished: { type: [Number, String], default: null },
  operatingHours: { type: Object, default: null },
  primaryColor: { type: String, default: null },
  viewListLabel: { type: String, default: '' },
  heroFallbackTitle: { type: String, default: 'Our catalog' },
  hasAbout: { type: Boolean, default: false },
  viewer: { type: Object, default: () => ({ is_owner: false }) },
  ownerViewMode: { type: Boolean, default: true },
})

const hasBannerImage = computed(() => typeof props.bannerUrl === 'string' && props.bannerUrl.trim() !== '')

const heroTitle = computed(() => (props.name || props.heroFallbackTitle).trim() || props.heroFallbackTitle)
const heroSubtext = computed(() => (props.tagline || props.description || '').trim())

const establishedYearDisplay = computed(() => {
  const y = props.yearEstablished
  if (y == null || y === '') return null
  const n = Number(y)
  return Number.isFinite(n) && n > 0 ? n : null
})

const primaryHex = computed(() => props.primaryColor?.trim() || '#2563eb')

const noBannerTintStyle = computed(() => ({
  background: `linear-gradient(135deg, ${primaryHex.value} 0%, transparent 55%)`,
}))

const fallbackIconBoxStyle = computed(() => ({
  backgroundColor: `${primaryHex.value}14`,
}))

const secondaryCtaClass = computed(() =>
  hasBannerImage.value
    ? 'border-white/40 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 focus-visible:ring-white focus-visible:ring-offset-slate-900'
    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
)
</script>

<style scoped>
.font-condensed {
  font-family: 'Roboto Condensed', ui-sans-serif, system-ui, sans-serif;
}
</style>
